import axios from "axios";
import { log } from "@/lib/logger";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
export const API_BASE = `${BACKEND_URL}/api`;

/**
 * Two shared Axios instances.
 *
 * `api` (withCredentials: true)
 *   Sends the admin httpOnly session cookie (issued by POST /api/admin/login)
 *   so admin-protected endpoints (/api/admin/*) can authenticate. No JWTs are
 *   ever stored in localStorage / sessionStorage / JS memory on the client —
 *   the cookie is the sole source of truth for the admin session.
 *
 * `publicApi` (withCredentials: false)
 *   Used for everything a visitor does that does NOT require authentication:
 *   POST /api/leads, GET /api/settings, GET /api/brochures, GET /api/directors,
 *   GET /api/media, etc. We deliberately drop credentials here for two reasons:
 *     1. Security — public requests should never carry admin session cookies,
 *        even if they exist in the browser jar.
 *     2. CORS — the platform's edge proxy rewrites `Access-Control-Allow-Origin`
 *        to `*` on the response. Per CORS spec, a `*` Allow-Origin combined
 *        with `withCredentials: true` is REJECTED by the browser. Dropping
 *        credentials on public calls keeps the browser happy without losing
 *        any functionality (public endpoints don't need a session anyway).
 */
export const api = axios.create({
  baseURL: API_BASE,
  headers: {
    "Content-Type": "application/json",
  },
  timeout: 20000,
  withCredentials: true,
});

export const publicApi = axios.create({
  baseURL: API_BASE,
  headers: {
    "Content-Type": "application/json",
  },
  timeout: 20000,
  withCredentials: false,
});

// ----- Leads (PUBLIC) -----
export const submitLead = async (payload) => {
  const res = await publicApi.post("/leads", payload);
  return res.data;
};

// =====================================================================
// Brochures — admin-curated PDF library
//
// Public:
//   fetchBrochures()              → list of {id,title,description,url,...}
//   brochureFileUrl(id)           → absolute URL for an <a href> / window.open
//   triggerBrochureFileDownload() → cross-origin-safe blob download (per file)
//
// Admin (requires httpOnly session cookie):
//   fetchAllBrochuresAdmin()
//   uploadBrochures(files[])      → multi-file FormData POST
//   updateBrochure(id, payload)   → JSON PATCH-style update (title/desc/order)
//   reorderBrochures(items[])     → bulk reorder
//   deleteBrochure(id)
// =====================================================================

export const fetchBrochures = async () => {
  const res = await publicApi.get("/brochures");
  return res.data;
};

export const fetchAllBrochuresAdmin = async () => {
  const res = await api.get("/admin/brochures");
  return res.data;
};

export const brochureFileUrl = (brochureId) =>
  `${API_BASE}/brochures/file/${brochureId}`;

/**
 * Cross-origin-safe per-file download.
 *
 * Anchor-with-download is silently ignored by most browsers for cross-origin
 * URLs (which is our case — frontend and backend live on different
 * sub-domains). We fetch the PDF as a Blob, create a same-origin blob: URL,
 * then click an anchor pointing at it. Works on Chrome / Safari / Firefox /
 * Edge / mobile WebKit. If that fails (e.g. CORS misconfig) we fall back to
 * `window.open()`.
 */
export const triggerBrochureFileDownload = async (brochure) => {
  const url = brochureFileUrl(brochure.id);
  const filename = `${(brochure.title || "brochure").replace(/[^A-Za-z0-9._ -]+/g, "").trim() || "brochure"}.pdf`;
  try {
    const response = await fetch(url, {
      method: "GET",
      cache: "no-cache",
      credentials: "omit",
    });
    if (!response.ok) throw new Error(`HTTP ${response.status}`);
    const blob = await response.blob();
    const objUrl = URL.createObjectURL(blob);
    const a = document.createElement("a");
    a.href = objUrl;
    a.download = filename;
    a.style.display = "none";
    document.body.appendChild(a);
    a.click();
    setTimeout(() => {
      try {
        document.body.removeChild(a);
      } catch (cleanupErr) {
        log.debug("[brochures] anchor cleanup noop:", cleanupErr.message);
      }
      URL.revokeObjectURL(objUrl);
    }, 1500);
    return true;
  } catch (e) {
    log.error("[brochures] blob download failed, falling back to direct open:", e);
    try {
      window.open(url, "_blank", "noopener,noreferrer");
    } catch (openErr) {
      log.error("[brochures] window.open also failed:", openErr);
      window.location.href = url;
    }
    return false;
  }
};

export const uploadBrochures = async (files, onUploadProgress = null) => {
  const form = new FormData();
  const arr = Array.from(files || []);
  if (!arr.length) {
    throw new Error("No files selected");
  }
  for (const f of arr) {
    form.append("files", f);
  }
  const res = await api.post("/admin/brochures", form, {
    headers: { "Content-Type": "multipart/form-data" },
    onUploadProgress,
    // Large multipart uploads need a longer timeout than the default 20s.
    timeout: 120000,
  });
  return res.data;
};

export const updateBrochure = async (brochureId, payload) => {
  const res = await api.put(`/admin/brochures/${brochureId}`, payload);
  return res.data;
};

export const reorderBrochures = async (items) => {
  const res = await api.post("/admin/brochures/reorder", { items });
  return res.data;
};

export const deleteBrochure = async (brochureId) => {
  const res = await api.delete(`/admin/brochures/${brochureId}`);
  return res.data;
};

// ----- Admin auth -----
export const adminLogin = async (username, password) => {
  // The backend response also sets an httpOnly cookie that the browser uses
  // for all subsequent admin requests. The returned `access_token` is kept
  // only for API tooling parity; the SPA itself does NOT read it.
  const res = await api.post("/admin/login", { username, password });
  return res.data;
};

export const adminLogout = async () => {
  try {
    await api.post("/admin/logout");
  } catch (e) {
    // 401 means we were already signed out — safe to ignore.
    if (e?.response?.status !== 401) {
      log.warn("[adminLogout] non-fatal error:", e);
    }
  }
};

export const verifyAdmin = async () => {
  try {
    const res = await api.get("/admin/verify");
    return res.data?.valid === true;
  } catch {
    return false;
  }
};

// ----- Leads (admin) -----
export const fetchLeads = async (filters = {}) => {
  const params = {};
  if (filters.project) params.project = filters.project;
  if (filters.source) params.source = filters.source;
  const res = await api.get("/leads", { params });
  return res.data;
};
export const fetchLeadStats = async () => {
  const res = await api.get("/leads/stats");
  return res.data;
};
export const deleteLead = async (leadId) => {
  const res = await api.delete(`/leads/${leadId}`);
  return res.data;
};

// ----- Site Settings -----
export const fetchSiteSettings = async () => {
  const res = await publicApi.get("/settings");
  return res.data;
};
export const updateSiteSettings = async (payload) => {
  const res = await api.put("/admin/settings", payload);
  return res.data;
};

// ----- Media library -----
export const fetchAllMedia = async () => {
  const res = await publicApi.get("/media");
  return res.data;
};
export const fetchMediaByKey = async (key) => {
  try {
    const res = await publicApi.get(`/media/by-key/${encodeURIComponent(key)}`);
    return res.data;
  } catch (e) {
    if (e?.response?.status === 404) return null;
    throw e;
  }
};
export const uploadMedia = async (file, key = null, altText = null, onUploadProgress = null) => {
  const form = new FormData();
  form.append("file", file);
  if (key) form.append("key", key);
  if (altText) form.append("altText", altText);
  const res = await api.post("/admin/media", form, {
    headers: { "Content-Type": "multipart/form-data" },
    onUploadProgress,
  });
  return res.data;
};
export const deleteMedia = async (mediaId) => {
  const res = await api.delete(`/admin/media/${mediaId}`);
  return res.data;
};
export const updateMedia = async (mediaId, { key, altText }) => {
  const form = new FormData();
  if (key !== undefined && key !== null) form.append("key", key);
  if (altText !== undefined && altText !== null) form.append("altText", altText);
  const res = await api.put(`/admin/media/${mediaId}`, form, {
    headers: { "Content-Type": "multipart/form-data" },
  });
  return res.data;
};

// ----- Directors -----
export const fetchDirectors = async () => {
  const res = await publicApi.get("/directors");
  return res.data;
};
export const fetchAllDirectorsAdmin = async () => {
  const res = await api.get("/directors", { params: { include_inactive: true } });
  return res.data;
};
export const createDirector = async (payload) => {
  const res = await api.post("/admin/directors", payload);
  return res.data;
};
export const updateDirector = async (id, payload) => {
  const res = await api.put(`/admin/directors/${id}`, payload);
  return res.data;
};
export const deleteDirector = async (id) => {
  const res = await api.delete(`/admin/directors/${id}`);
  return res.data;
};

// Helper: resolve a public-relative media URL into an absolute URL.
export const resolveMediaUrl = (url) => {
  if (!url) return null;
  if (url.startsWith("http://") || url.startsWith("https://")) return url;
  if (url.startsWith("/api/")) return `${BACKEND_URL}${url}`;
  if (url.startsWith("/")) return `${BACKEND_URL}${url}`;
  return url;
};
